import qs from 'qs';
import AxiosInstance from './AxiosInterceptor';
import {
  IApplicability,
  ICategory,
  IClientReq
} from 'store/ducks/ClientReq/types';
import {
  IAssignedEls,
  IAssignedEvidences,
  ISelectedOptions
} from 'store/ducks/ComplienceEst/types';
import { IGapsInfo, ISaveInfo } from 'store/ducks/Gaps/types';
import { IResetPass, LanguageList } from 'store/ducks/globalTypes';
import { ISolution } from 'store/ducks/Solutions/types';
import { ITask } from 'store/ducks/Tasks/types';
import { IEvidenceToShow } from 'store/ducks/Evidences/types';
import {
  AllocateProcessRiskControlRequest,
  AllocateProcessRiskRequest,
  Scope
} from 'store/ducks/InternalControls/types';
import { IEntityIndustryAllocationPayload } from 'store/ducks/Global/types';
import { generateUniqueId } from './hooks/generateUniqueId';

const ApiService = {
  // Helper function to get first entity ID
  getFirstEntityId: async (): Promise<number | null> => {
    try {
      const response = await AxiosInstance({
        url: '/api/get-entities',
        method: 'get',
        params: { language: 'en' }
      });
      const entities = response.data;
      return entities && entities.length > 0 ? entities[0].id : null;
    } catch (error) {
      console.error('Failed to fetch entity ID:', error);
      return null;
    }
  },
  login: (data: { email: string; password: string; schema_id: string }) =>
    AxiosInstance({
      url: '/login',
      method: 'post',
      data
    }),
  superAdminLogin: (data: { email: string; password: string }) =>
    AxiosInstance({
      url: '/api/superuser-login',
      method: 'post',
      data
    }),
  requestLoginEmail: (email: string, password: string) =>
    AxiosInstance({
      url: '/login',
      method: 'post',
      data: { email, password }
    }),
  verifyLoginToken: (token: string) =>
    AxiosInstance({
      url: '/verify-login',
      method: 'get',
      params: { token }
    }),
  logout: () =>
    AxiosInstance({
      url: '/logout',
      method: 'post'
    }),
  superuserLogout: () =>
    AxiosInstance({
      url: '/superuser-logout',
      method: 'post'
    }),
  forgotPassword: (email: string) =>
    AxiosInstance({
      url: '/forgot-password',
      method: 'post',
      data: { email }
    }),

  resetPassword: (formData: IResetPass) =>
    AxiosInstance({
      url: '/reset-password',
      method: 'post',
      data: formData
    }),
  getPrioritization: (lang: LanguageList) =>
    AxiosInstance({
      url: '/gaps/prioritization',
      method: 'get',
      params: { language: lang }
    }),
  getGapSource: (lang: LanguageList) =>
    AxiosInstance({
      url: '/gaps/gap_source',
      method: 'get',
      params: { language: lang }
    }),
  getOwnersList: () =>
    AxiosInstance({
      url: '/filters/ownership',
      method: 'get'
    }),
  getTypesList: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters/task_types',
      method: 'get',
      params: { language: lang }
    }),
  getStatuses: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters/task_statuses',
      method: 'get',
      params: { language: lang }
    }),
  getFilters: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters',
      method: 'get',
      params: { language: lang }
    }),
  addClientReq: (data: IClientReq[], lang: LanguageList) =>
    AxiosInstance({
      url: '/client_input',
      method: 'post',
      params: { language: lang },
      data
    }),

  changeApplicability: (data: IApplicability[], lang: LanguageList) =>
    AxiosInstance({
      url: '/update_applic',
      method: 'post',
      params: { language: lang },
      data
    }),
  changeCategory: (data: ICategory[], lang: LanguageList) =>
    AxiosInstance({
      url: '/update_category',
      method: 'post',
      params: { language: lang },
      data
    }),
  getAuditFindingMapping: (
    lang: LanguageList,
    filters: Record<string, string[]>
  ) =>
    AxiosInstance({
      url: '/gaps/fetch/afg',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  getEvidenceMapping: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/gaps/fetch/evd',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  getGapsDescription: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/gaps/fetch/all',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  getAdditionalInfo: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/gaps/fetch/info',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  addGaps: (data: IGapsInfo[], lang: LanguageList) =>
    AxiosInstance({
      url: '/gaps/save',
      method: 'post',
      params: { language: lang },
      data
    }),
  addGapsInfo: (data: ISaveInfo[], lang: LanguageList) =>
    AxiosInstance({
      url: '/gaps/save/info',
      method: 'post',
      params: { language: lang },
      data
    }),
  removeGap: (id: string | number) =>
    AxiosInstance({
      url: '/gaps/save',
      method: 'post',
      data: [{ action: 'DELETE', gap_id: id }]
    }),
  // getComments: (table: CommentTable) =>
  //   AxiosInstance({
  //     url: '/comment',
  //     method: 'get',
  //     params: { table }
  //   }),
  // addComment: (data: CommentItem[]) =>
  //   AxiosInstance({
  //     url: '/comment',
  //     method: 'post',
  //     data
  //   }),
  // removeComment: (comment_id: number) =>
  //   AxiosInstance({
  //     url: '/comment',
  //     method: 'delete',
  //     data: { comment_id }
  //   }),
  removeAudits: (params: { afg_id: number }) =>
    AxiosInstance({
      url: '/gaps/delete/afg',
      method: 'post',
      params
    }),
  removeEvidences: (params: { evd_id: number }) =>
    AxiosInstance({
      url: '/gaps/delete/evd',
      method: 'post',
      params
    }),
  applyFilters: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),
  setFiltersModule: (
    data: ISelectedOptions,
    lang: LanguageList,
    filters: Record<string, string[]>
  ) =>
    AxiosInstance({
      url: '/gaps/fetch/evd',
      method: 'get',
      params: { language: lang, ...filters },
      data
    }),
  saveAudits: (data: IAssignedEls[]) =>
    AxiosInstance({
      url: '/gaps/afg/save',
      method: 'post',
      data
    }),
  saveEvidences: (data: IAssignedEvidences[]) =>
    AxiosInstance({
      url: '/gaps/evd/save',
      method: 'post',
      data
    }),
  getAuditFindings: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/gaps/unassigned_audits',
      method: 'get',
      params: { language: lang, ...filters }
    }),
  getUnassignedEvidences: (
    lang: LanguageList,
    filters: Record<string, string[]>
  ) =>
    AxiosInstance({
      url: '/gaps/unassigned_evd',
      method: 'get',
      params: { language: lang, ...filters }
    }),
  getSolutions: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/solution/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),
  removeSolution: (sol_id: number | string) =>
    AxiosInstance({
      url: '/solution/save',
      method: 'post',
      data: [{ action: 'DELETE', sol_id: sol_id }]
    }),
  getSolutionApproverList: () =>
    AxiosInstance({
      url: '/solution/approvers',
      method: 'get'
    }),
  getSolutionTypes: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters/sol_types',
      method: 'get',
      params: { language: lang }
    }),
  saveSolutions: (solData: ISolution[], lang: LanguageList) => {
    const cleanedData = solData.map((solution) => {
      return {
        ...solution,
        sol_id: solution.sol_id ?? generateUniqueId()
      };
    });
    return AxiosInstance({
      url: '/solution/save',
      method: 'post',
      params: { language: lang },
      data: cleanedData
    });
  },
  approveSolution: (solId: number | string) =>
    AxiosInstance({
      url: `/solution/approve/${solId}`,
      method: 'post'
    }),
  rejectSolution: (solId: number | string) =>
    AxiosInstance({
      url: `/solution/reject/${solId}`,
      method: 'post'
    }),
  getTasks: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/tasks/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  saveTasks: (data: ITask[], lang: LanguageList) =>
    AxiosInstance({
      url: '/tasks/save',
      method: 'post',
      params: { language: lang },
      data
    }),
  getEvidences: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/evidences/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),
  removeEvidenceDef: (evd_id: number | string) =>
    AxiosInstance({
      url: '/evidences/save',
      method: 'post',
      data: [{ action: 'DELETE', evd_id: evd_id }]
    }),
  saveEvidencesDef: (data: any[], lang: LanguageList) =>
    AxiosInstance({
      url: '/evidences/save',
      method: 'post',
      params: { language: lang },
      data
    }),
  getEvidenceDefApproverList: () =>
    AxiosInstance({
      url: '/evidences/approvers',
      method: 'get'
    }),
  approveEvidence: (evdId: number | string) =>
    AxiosInstance({
      url: `/evidences/approve/${evdId}`,
      method: 'post'
    }),
  rejectEvidence: (evdId: number | string) =>
    AxiosInstance({
      url: `/evidences/reject/${evdId}`,
      method: 'post'
    }),
  uploadEvidenceFiles: (formData: FormData) =>
    AxiosInstance({
      url: '/evidences/upload',
      method: 'post',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }),
  downloadEvidenceFile: (evd_id: number | string) =>
    AxiosInstance({
      url: `/evidences/download/${evd_id}/`,
      method: 'get',
      responseType: 'blob'
    }),
  getMngmPrioritization: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters/prioritization',
      method: 'get',
      params: { language: lang }
    }),

  getAuditSource: (lang: LanguageList) =>
    AxiosInstance({
      url: '/filters/audit_source',
      method: 'get',
      params: { language: lang }
    }),

  getCategory: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/mgmt/fetch/categories',
      method: 'get',
      params: { ...filters, language: lang }
    }),

  getProduct: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/mgmt/fetch/product_functions',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  // getProduct: (lang: LanguageList) =>
  //   AxiosInstance({
  //     url: '/mgmt/fetch/product_functions',
  //     method: 'get',
  //     params: { language: lang }
  //   }),
  removeAuditFindings: (params: { afg_id: number }) =>
    AxiosInstance({
      url: '/mgmt/delete/afg',
      method: 'post',
      params
    }),
  getReports: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/report/fetch',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  getMgmtAudits: (lang: LanguageList, filters: Record<string, string[]>) =>
    AxiosInstance({
      url: '/mgmt/fetch/afg',
      method: 'get',
      params: { ...filters, language: lang }
    }),
  saveGapSource: (data: any) =>
    AxiosInstance({
      url: '/mgmt/save/gap_source',
      method: 'post',
      data
    }),
  saveAuditSource: (data: any) =>
    AxiosInstance({
      url: '/mgmt/save/audit_source',
      method: 'post',
      data
    }),
  saveRiskType: (data: any) =>
    AxiosInstance({
      url: '/mgmt/save/risk_type',
      method: 'post',
      data
    }),
  saveAuditFindings: (data: any, lang: LanguageList) =>
    AxiosInstance({
      url: '/mgmt/save/afg',
      method: 'post',
      params: {
        language: lang
      },
      data
    }),
  saveCategory: (data: any, lang: LanguageList) =>
    AxiosInstance({
      url: '/mgmt/save/category',
      method: 'post',
      params: { language: lang },
      data
    }),
  saveProduct: (data: any, lang: LanguageList) =>
    AxiosInstance({
      url: '/mgmt/save/product',
      method: 'post',
      params: { language: lang },
      data
    }),
  deleteCategory: (catId: number) =>
    AxiosInstance({
      url: '/mgmt/delete/category',
      method: 'post',
      params: { category_id: catId }
    }),

  getUserFunctionRoles: (active: boolean) =>
    AxiosInstance({
      url: '/api/user-function-roles',
      method: 'post',
      data: { active }
    }),

  getActiveUsers: () =>
    AxiosInstance({
      url: '/api/get-active-users',
      method: 'get'
    }),

  getActiveInactiveOptions: () =>
    AxiosInstance({
      url: '/api/active-inactive-dropdown',
      method: 'post',
      data: {}
    }),

  getUserFunctions: (data: any) => {
    return AxiosInstance({
      url: '/api/get-user-functions',
      method: 'get',
      params: {
        user_id: data.user_id
      }
    });
  },

  signup: (data: any) =>
    AxiosInstance({
      url: '/signup',
      method: 'post',
      data
    }),

  fetchUserActivityStatus: (username: string) =>
    AxiosInstance({
      url: '/api/toggle_user_status',
      method: 'post',
      data: { username }
    }),

  assignUserFunctions: (user_id: number, function_ids: any) =>
    AxiosInstance({
      url: '/api/assign-user-functions',
      method: 'post',
      data: { user_id, function_ids }
    }),

  removeUserFunctions: (user_id: number, function_ids: any) =>
    AxiosInstance({
      url: '/api/remove-user-functions',
      method: 'post',
      data: { user_id, function_ids }
    }),

  getControlReports: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/control-reports?${queryString}`
        : '/api/control-reports',
      method: 'get'
    }),

  getOwnersBPR: () =>
    AxiosInstance({
      url: '/api/get-owners',
      method: 'get'
    }),

  getApproversBPR: () =>
    AxiosInstance({
      url: '/api/get-approvers',
      method: 'get'
    }),

  getControlTypesBPR: () =>
    AxiosInstance({
      url: '/api/get-control-types',
      method: 'get'
    }),

  getControlLevelsBPR: () =>
    AxiosInstance({
      url: '/api/get-control-levels',
      method: 'get'
    }),

  getPerformerToDBPR: () =>
    AxiosInstance({
      url: '/api/get-performer-tod',
      method: 'get'
    }),

  getControlNatureBPR: () =>
    AxiosInstance({
      url: '/api/get-control-nature',
      method: 'get'
    }),

  getControlFrequencyBPR: () =>
    AxiosInstance({
      url: '/api/get-control-frequency',
      method: 'get'
    }),

  getPerformerToEBPR: () =>
    AxiosInstance({
      url: '/api/get-performer-toe',
      method: 'get'
    }),

  getControlPerformerBPR: () =>
    AxiosInstance({
      url: '/api/get-control-performer',
      method: 'get'
    }),

  getOperatingEffectivenessBPR: () =>
    AxiosInstance({
      url: '/api/get-operating-effectiveness',
      method: 'get'
    }),

  getDesignEffectivenessBPR: () =>
    AxiosInstance({
      url: '/api/get-design-effectiveness',
      method: 'get'
    }),

  getControlStatusBPR: () =>
    AxiosInstance({
      url: '/api/get-control-status',
      method: 'get'
    }),

  getControlOriginBPR: () =>
    AxiosInstance({
      url: '/api/get-control-origin',
      method: 'get'
    }),

  getControlCustodianBPR: () =>
    AxiosInstance({
      url: '/api/get-control-custodian',
      method: 'get'
    }),

  getControlsBPR: (filters?: {
    entity_id?: number | null;
    group_id?: number | null;
    division_id?: number | null;
    region_id?: number | null;
    legal_entity_id?: number | null;
    business_unit_id?: number | null;
    owner_id?: number | null;
    bru_assigned?: string;
  }) =>
    AxiosInstance({
      url: '/api/get-control1',
      method: 'get',
      params: filters ? filters : {}
    }),

  getControlsTestBPR: (filters?: {
    entity_id?: number;
    group_id?: number;
    division_id?: number;
    region_id?: number;
    legal_entity_id?: number;
    business_unit_id?: number;
    owner_id?: number;
    bru_assigned?: string;
  }) =>
    AxiosInstance({
      url: '/api/get-control2',
      method: 'get',
      params: filters ? filters : {}
    }),

  getControlsTestOperationalBPR: (filters?: {
    entity_id?: number;
    group_id?: number;
    division_id?: number;
    region_id?: number;
    legal_entity_id?: number;
    business_unit_id?: number;
    owner_id?: number;
    bru_assigned?: string;
  }) =>
    AxiosInstance({
      url: '/api/get-control3',
      method: 'get',
      params: filters ? filters : {}
    }),

  getTestingMethodBPR: () =>
    AxiosInstance({
      url: '/api/get-testing-method',
      method: 'get'
    }),

  getRisksBPR: (filters?: {
    entity_id?: number | null;
    group_id?: number | null;
    division_id?: number | null;
    region_id?: number | null;
    legal_entity_id?: number | null;
    business_unit_id?: number | null;
    bru_assigned?: string;
  }) =>
    AxiosInstance({
      url: '/api/get-risk-maintenance',
      method: 'get',
      params: filters ? filters : {}
    }),

  getEntitiesForBPR: (lang: LanguageList) =>
    AxiosInstance({
      url: '/api/get-entities',
      method: 'get',
      params: { language: lang }
    }),

  getProbabilityOfOccurenceBPR: () =>
    AxiosInstance({
      url: '/api/get-probability-of-occurrence',
      method: 'get'
    }),
  getSpeedOfOcuuranceBPR: () =>
    AxiosInstance({
      url: '/api/get-speed-of-occurrence',
      method: 'get'
    }),
  getGrossRisksBPR: () =>
    AxiosInstance({
      url: '/api/get-gross-risks',
      method: 'get'
    }),

  getUpdateCyclesBPR: () =>
    AxiosInstance({
      url: '/api/get-update-cycles',
      method: 'get'
    }),

  getSeverityImpactStrengthBPR: () =>
    AxiosInstance({
      url: '/api/get-severity-impact-strength',
      method: 'get'
    }),

  getResidualRiskBPR: () =>
    AxiosInstance({
      url: '/api/get-residual-risk',
      method: 'get'
    }),
  getRiskStatusBPR: () =>
    AxiosInstance({
      url: '/api/get-risk-status',
      method: 'get'
    }),
  getRiskTrendBPR: () =>
    AxiosInstance({
      url: '/api/get-risk-trend',
      method: 'get'
    }),
  saveControlsBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-control1',
      method: 'post',
      data: payload
    }),
  saveControlsTestBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-control2',
      method: 'post',
      data: payload
    }),
  saveControlsTestoperationalBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-control3',
      method: 'post',
      data: payload
    }),
  saveRisksBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-risk-maintenance',
      method: 'post',
      data: payload
    }),
  getPorcessTypeBPR: () =>
    AxiosInstance({
      url: '/api/get-process-type',
      method: 'get'
    }),
  getBusinessProcessL1BPR: (entityId?: number) =>
    AxiosInstance({
      url: '/api/get-business-process-l1',
      method: 'get',
      params: entityId ? { entity_id: entityId } : {}
    }),
  getBusinessProcessL2BPR: (entityId?: number) =>
    AxiosInstance({
      url: '/api/get-business-process-l2',
      method: 'get',
      params: entityId ? { entity_id: entityId } : {}
    }),

  getBusinessProcessL3BPR: (entityId?: number) =>
    AxiosInstance({
      url: '/api/get-business-process-l3',
      method: 'get',
      params: entityId ? { entity_id: entityId } : {}
    }),

  getBusinessProcessL4BPR: (filters?: {
    entity_id?: number;
    group_id?: number;
    division_id?: number;
    region_id?: number;
    legal_entity_id?: number;
    business_unit_id?: number;
  }) =>
    AxiosInstance({
      url: '/api/get-business-process-l4',
      method: 'get',
      params: filters ? filters : {}
    }),

  saveProcessBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-business-process-l4',
      method: 'post',
      data: payload
    }),

  saveProcessL1BPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-business-process-l1',
      method: 'post',
      data: payload
    }),

  saveProcessL2BPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-business-process-l2',
      method: 'post',
      data: payload
    }),

  saveProcessL3BPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-business-process-l3',
      method: 'post',
      data: payload
    }),

  fetchGlobalUsers: () =>
    AxiosInstance({
      url: '/api/get-global-users',
      method: 'get'
    }),

  fetchGlobalCompanies: () =>
    AxiosInstance({
      url: '/api/get-global-companies',
      method: 'get'
    }),

  signupSuperUser: (data: any) =>
    AxiosInstance({
      url: '/api/signup-superuser',
      method: 'post',
      data
    }),

  postGlobalCompanies: (data: any) =>
    AxiosInstance({
      url: '/api/insert-global-company',
      method: 'post',
      data
    }),

  softDeleteUser: (email: number, schema_id: any) =>
    AxiosInstance({
      url: '/api/superadmin-soft-delete-user',
      method: 'post',
      data: { email, schema_id }
    }),

  createClientSchema: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('sql_file', file, file.name);
    return AxiosInstance.post('/api/create-client-schema', fd);
  },

  uploadExcelEntityTypeActivity: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('file', file, file.name);
    return AxiosInstance.post('/api/upload-excel-entity-type-activity', fd);
  },

  uploadExcelAccessControl: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('file', file, file.name);
    return AxiosInstance.post('/api/upload-access-control-excel', fd);
  },

  uploadParentData: (schemaId: string, file: File) => {
    const fd = new FormData();
    fd.append('schema_id', schemaId);
    fd.append('excel_file', file, file.name);
    return AxiosInstance.post('/api/upload-parent-data', fd);
  },

  saveBruRiskControl: (data: any) =>
    AxiosInstance({
      url: '/api/bru-risk-control',
      method: 'post',
      data
    }),

  getBruContentTypes: () =>
    AxiosInstance({
      url: '/api/get-bru-content-types',
      method: 'get'
    }),

  softDeleteControl: (control_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-control',
      method: 'post',
      data: { control_id }
    }),

  softDeleteControl2: (control_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-control2',
      method: 'post',
      data: { control_id }
    }),

  softDeleteRiskMaintenance: (risk_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-risk-maintenance',
      method: 'post',
      data: { risk_id }
    }),

  softDeleteBusinessProcessL4: (business_process_l4_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-business-process-l4',
      method: 'post',
      data: { business_process_l4_id }
    }),

  softDeleteBusinessProcessL1: (business_process_l1_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-business-process-l1',
      method: 'post',
      data: { business_process_l1_id }
    }),

  softDeleteBusinessProcessL2: (business_process_l2_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-business-process-l2',
      method: 'post',
      data: { business_process_l2_id }
    }),

  softDeleteBusinessProcessL3: (business_process_l3_id: number) =>
    AxiosInstance({
      url: '/api/soft-delete-business-process-l3',
      method: 'post',
      data: { business_process_l3_id }
    }),

  verifySuperAdminLogin: (token: string) =>
    AxiosInstance({
      url: '/api/verify-superuser-login',
      method: 'get',
      params: { token }
    }),

  getTestTypesBPR: () =>
    AxiosInstance({
      url: '/api/get-test-types',
      method: 'get'
    }),

  getTestObjectivenessBPR: () =>
    AxiosInstance({
      url: '/api/get-test-objectiveness',
      method: 'get'
    }),

  getTestDescriptionBPR: () =>
    AxiosInstance({
      url: '/api/get-test-description',
      method: 'get'
    }),

  getTestResultBPR: () =>
    AxiosInstance({
      url: '/api/get-test-result',
      method: 'get'
    }),

  getEvidenceTypeBPR: () =>
    AxiosInstance({
      url: '/api/get-evidence-type',
      method: 'get'
    }),

  getGroupBPR: () =>
    AxiosInstance({
      url: '/api/get-group',
      method: 'get'
    }),

  getDivisionBPR: () =>
    AxiosInstance({
      url: '/api/get-division',
      method: 'get'
    }),

  getRegionBPR: () =>
    AxiosInstance({
      url: '/api/get-region',
      method: 'get'
    }),

  getLegalEntitiesBPR: () =>
    AxiosInstance({
      url: '/api/get-legal-entities',
      method: 'get'
    }),

  getBusinessUnitBPR: () =>
    AxiosInstance({
      url: '/api/get-business-unit',
      method: 'get'
    }),

  getProcessCriticalitiesBPR: () =>
    AxiosInstance({
      url: '/api/get-process-criticalities',
      method: 'get'
    }),

  getReasonsForControlStatusBPR: () =>
    AxiosInstance({
      url: '/api/get-reason-for-control-status',
      method: 'get'
    }),

  getControlPrerequisites: () =>
    AxiosInstance({
      url: '/api/get-control-prerequisites',
      method: 'get'
    }),

  saveDivision: (payload: { data_list: { id: number; group_id: number }[] }) =>
    AxiosInstance({
      url: '/api/insert-division',
      method: 'post',
      data: payload
    }),

  saveRegion: (payload: { data_list: { id: number; division_id: number }[] }) =>
    AxiosInstance({
      url: '/api/insert-region',
      method: 'post',
      data: payload
    }),

  saveLegalEntity: (payload: {
    data_list: { id: number; region_id: number }[];
  }) =>
    AxiosInstance({
      url: '/api/insert-legal-entity',
      method: 'post',
      data: payload
    }),

  saveBusinessUnit: (payload: {
    data_list: { id: number; legal_entity_id: number }[];
  }) =>
    AxiosInstance({
      url: '/api/insert-business-unit',
      method: 'post',
      data: payload
    }),

  getProcessOverview: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/process-overview?${queryString}`
        : '/api/process-overview',
      method: 'get'
    }),

  getRiskInventory: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/risk-inventory?${queryString}`
        : '/api/risk-inventory',
      method: 'get'
    }),

  getProcessInventory: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/process-inventory?${queryString}`
        : '/api/process-inventory',
      method: 'get'
    }),

  getGroupOrganization: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/group-organization?${queryString}`
        : '/api/group-organization',
      method: 'get'
    }),

  // OLD API - Commented out for migration
  // getRisksControlsGaps: (
  //   filters: Record<string, string | number | undefined>
  // ) =>
  //   AxiosInstance({
  //     url: '/api/risks-controls-gaps',
  //     method: 'get',
  //     params: filters
  //   }),

  getProcessRiskControl: (queryString?: string) =>
    AxiosInstance({
      url: queryString
        ? `/api/process-risk-control?${queryString}`
        : '/api/process-risk-control',
      method: 'get'
    }),

  getBruAuditGaps: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/bru_audit_gaps/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  getBruEvidences: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/bru_evidences/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  getBruGapsAuditsSolutions: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/bru_gaps_audits_solutions/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  getSolutionsTasks: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/solutions_tasks/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  getGapsFilter: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/gaps/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  getAuditFindingsFilter: (
    lang: LanguageList,
    filters: Record<string, string | undefined>
  ) =>
    AxiosInstance({
      url: '/audit/findings/filter',
      method: 'get',
      params: { language: lang, ...filters }
    }),

  allocateProcessRisk: (payload: AllocateProcessRiskRequest) =>
    AxiosInstance.post('/api/allocate-process-risk', payload),

  allocateProcessRiskControl: (payload: AllocateProcessRiskControlRequest) =>
    AxiosInstance.post('/api/allocate-process-risk-control', payload),

  getIndustriesByHierarchy: (params: {
    group_id: number;
    division_id: number;
    region_id: number;
    legal_entity_id: number;
    business_unit_id: number;
    lang: LanguageList;
  }) =>
    AxiosInstance({
      url: '/api/get-industries-by-hierarchy',
      method: 'get',
      params
    }),

  getModulesByIndustries: (params: { lang: LanguageList; ind_id: number }) =>
    AxiosInstance({
      url: '/api/get-modules-by-industries',
      method: 'get',
      params
    }),

  allocateIndustryTypeToGroups: (data: IEntityIndustryAllocationPayload) =>
    AxiosInstance({
      url: '/api/map-industries-to-group',
      method: 'post',
      data
    }),

  allocateIndustryTypeToDivisions: (data: IEntityIndustryAllocationPayload) =>
    AxiosInstance({
      url: '/api/map-industries-to-division',
      method: 'post',
      data
    }),

  allocateIndustryTypeToRegions: (data: IEntityIndustryAllocationPayload) =>
    AxiosInstance({
      url: '/api/map-industries-to-region',
      method: 'post',
      data
    }),

  allocateIndustryTypeToLegalEntities: (
    data: IEntityIndustryAllocationPayload
  ) =>
    AxiosInstance({
      url: '/api/map-industries-to-legal-entity',
      method: 'post',
      data
    }),

  allocateIndustryTypeToBusinessUnits: (
    data: IEntityIndustryAllocationPayload
  ) =>
    AxiosInstance({
      url: '/api/map-industries-to-business-unit',
      method: 'post',
      data
    }),

  listIndustriesByHierarchy: (data: IEntityIndustryAllocationPayload) =>
    AxiosInstance({
      url: '/api/list-industries-by-hierarchy',
      method: 'post',
      params: { language: 'en' },
      data
    }),

  getControlsAttestationBPR: (filters?: {
    group_id?: number;
    division_id?: number;
    region_id?: number;
    legal_entity_id?: number;
    business_unit_id?: number;
    owner_id?: number;
    bru_assigned?: string;
  }) =>
    AxiosInstance({
      url: '/api/get-control4',
      method: 'get',
      params: filters ? filters : {}
    }),

  saveControlsAttestationBPR: (payload: any) =>
    AxiosInstance({
      url: '/api/insert-control4',
      method: 'post',
      data: payload
    }),

  getControlAttestationEffectivenessBPR: () =>
    AxiosInstance({
      url: '/api/get-control-attestation-effectiveness',
      method: 'get'
    }),

  getTestStatusesBPR: () =>
    AxiosInstance({
      url: '/api/get-test-statuses',
      method: 'get'
    }),

  getControlEffectivenessBPR: () =>
    AxiosInstance({
      url: '/api/get-control-effectiveness',
      method: 'get'
    }),

  mapGroupsToPerson: (person_id: number, group_ids: number[]) =>
    AxiosInstance({
      url: '/api/map-groups-to-person',
      method: 'post',
      data: { person_id, group_ids }
    }),

  mapDivisionsToPerson: (person_id: number, division_ids: number[]) =>
    AxiosInstance({
      url: '/api/map-divisions-to-person',
      method: 'post',
      data: { person_id, division_ids }
    }),

  mapRegionsToPerson: (person_id: number, region_ids: number[]) =>
    AxiosInstance({
      url: '/api/map-regions-to-person',
      method: 'post',
      data: { person_id, region_ids }
    }),

  mapLegalEntitiesToPerson: (person_id: number, legal_entity_ids: number[]) =>
    AxiosInstance({
      url: '/api/map-legal-entities-to-person',
      method: 'post',
      data: { person_id, legal_entity_ids }
    }),

  mapBusinessUnitsToPerson: (person_id: number, business_unit_ids: number[]) =>
    AxiosInstance({
      url: '/api/map-business-units-to-person',
      method: 'post',
      data: { person_id, business_unit_ids }
    }),

  getAllGroup: () =>
    AxiosInstance({
      url: '/api/get-all-group',
      method: 'get'
    }),

  getAllDivision: () =>
    AxiosInstance({
      url: '/api/get-all-division',
      method: 'get'
    }),

  getAllRegion: () =>
    AxiosInstance({
      url: '/api/get-all-region',
      method: 'get'
    }),

  getAllLegalEntities: () =>
    AxiosInstance({
      url: '/api/get-all-legal-entities',
      method: 'get'
    }),

  getAllBusinessUnites: () =>
    AxiosInstance({
      url: '/api/get-all-business-unit',
      method: 'get'
    }),

  getAllActiveUsers: () =>
    AxiosInstance({
      url: '/api/get-all-active-users',
      method: 'get'
    }),

  getgroupsByUser: (user_id: number) =>
    AxiosInstance({
      url: '/api/get-groups-by-user',
      method: 'get',
      params: { user_id }
    }),

  getDivisionsByUser: (user_id: number) =>
    AxiosInstance({
      url: '/api/get-divisions-by-user',
      method: 'get',
      params: { user_id }
    }),

  getRegionsByUser: (user_id: number) =>
    AxiosInstance({
      url: '/api/get-regions-by-user',
      method: 'get',
      params: { user_id }
    }),

  getLegalEntitiesByUser: (user_id: number) =>
    AxiosInstance({
      url: '/api/get-legal-entities-by-user',
      method: 'get',
      params: { user_id }
    }),

  getBusinessUnitsByUser: (user_id: number) =>
    AxiosInstance({
      url: '/api/get-business-units-by-user',
      method: 'get',
      params: { user_id }
    }),

  getMainOrganizations: () =>
    AxiosInstance({
      url: '/api/get-main-organizations',
      method: 'get'
    }),

  getOrgRisk: (scope: Scope & { bru_assigned?: string }) =>
    AxiosInstance({
      url: '/api/get-org-risk-maintenance',
      method: 'get',
      params: scope
    }),

  getOrgControl: (scope: Scope & { bru_assigned?: string }) =>
    AxiosInstance({
      url: '/api/get-org-control',
      method: 'get',
      params: scope
    }),

  getUserApplicationModules: () =>
    AxiosInstance({
      url: '/api/get-user-application-modules',
      method: 'get'
    }),

  getRiskMaintenance: (date?: string) =>
    AxiosInstance({
      url: '/api/get-risk-maintenance-report',
      method: 'get',
      params: date ? { date } : undefined
    }),

  getBruAssigned: () =>
    AxiosInstance({
      url: '/api/get-bru-assigned',
      method: 'get'
    }),

  getControlMaintenance: (date?: string) =>
    AxiosInstance({
      url: '/api/get-control-report',
      method: 'get',
      params: date ? { date } : undefined
    }),

  getSchemaTables: () =>
    AxiosInstance({
      url: '/api/get-schema-tables',
      method: 'get'
    }),

  checkDeleteDependency: (
    table_name: string,
    id: string | number,
    lang = 'en'
  ) =>
    AxiosInstance({
      url: '/api/check-delete-dependency',
      method: 'get',
      params: { table_name, id, lang }
    }),

  softDeleteRecord: (table_name: string, id: string | number) => {
    return AxiosInstance({
      url: '/api/soft-delete-record',
      method: 'post',
      params: { table_name, id, confirm: true }
    });
  },

  getControlEffectivenessReport: (date?: string) =>
    AxiosInstance({
      url: '/api/get-control-effectiveness-report',
      method: 'get',
      params: date ? { date } : undefined
    }),

  getBusinessProcessRiskControlReport: (date?: string) =>
    AxiosInstance({
      url: '/api/get-business-process-risk-control-report',
      method: 'get',
      params: date ? { date } : undefined
    }),

  mapIndustriesToModule: (params: { mod_id: number; industry_ids: number[] }) =>
    AxiosInstance({
      url: '/api/map-industries-to-module',
      method: 'post',
      data: params
    })
};

export default ApiService;
